import {FC, useCallback, useEffect, useState} from 'react';
import {Header} from '@components/Header';
import {Nav} from '@components/Nav';
import {VisuallyHidden} from '@components/VisuallyHidden';
import {Logo} from '@components/Logo';
import {ButtonGroup} from '@components/ButtonGroup';
import {Button} from '@components/Button';
import {Typography} from '@components/Typography';
import {Icon} from '@components/Icon';
import {FormattedMessage} from 'react-intl';
import Link from 'next/link';
import NextImage from 'next/image';
import {getLocalizedRoute, isRoute, routes} from '@utils/routes';
import {useSession} from 'next-auth/react';
import {Avatar} from '@components/Avatar';
import {SidebarLogin} from './SidebarLogin';
import {useUserNotificationCountsLazyQuery} from '@gql/query/user/counts.gql';
import {ButtonBadge} from '@components/ButtonBadge';
import {Badge} from '@components/Badge';
import {LoginRequiredAction} from '@liveComponents/Auth';
import Generic from '@liveComponents/LoginRequiredInitialPage/Generic';
import {InteractionLoadable} from '@liveComponents/Loadable';
import dynamic from 'next/dynamic';
import {useLocale} from '@utils/locale';
import {useRouter} from 'next/router';
import {SidebarMenuLogout} from './SidebarMenuLogout';
import {useMQ} from '@hooks/useMQ';

const SidebarMenu = dynamic(() => import('./SidebarMenu'), {ssr: false});

export const PageHeader: FC = () => {
    const locale = useLocale();
    const {data} = useSession();
    const router = useRouter();
    const isTabletMobile = useMQ('xlDown');

    const [getCounts, countData] = useUserNotificationCountsLazyQuery({
        fetchPolicy: 'network-only',
        pollInterval: 300000,
    });

    const [showMenuSidebar, setMenuSidebar] = useState<boolean>(false);
    const [showUserSidebar, setUserSidebar] = useState<boolean>(false);

    const closeSidebar = useCallback(() => {
        setUserSidebar(false);
        setMenuSidebar(false);
    }, []);

    useEffect(() => {
        if (data?.user) {
            getCounts({
                variables: {
                    user: data?.user.id,
                },
            });
        }
    }, [data?.user, getCounts]);

    return (
        <>
            <Header withBorder>
                <Header.Logo>
                    <Link href={getLocalizedRoute(routes.homepage, locale)}>
                        <a>
                            <VisuallyHidden>Bezrealitky</VisuallyHidden>
                            <Logo />
                        </a>
                    </Link>
                </Header.Logo>

                <Header.Nav>
                    <Nav as="ul">
                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.list, locale, {list: ''})} passHref>
                                <Nav.Link active={isRoute(router.asPath, routes.list, locale)}>
                                    <FormattedMessage defaultMessage="Nemovitosti" />
                                </Nav.Link>
                            </Link>
                        </Nav.Item>

                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.serviceCentreIntroCustomer, locale)} passHref>
                                <Nav.Link>
                                    <FormattedMessage defaultMessage="Tipy pro zájemce" />
                                </Nav.Link>
                            </Link>
                        </Nav.Item>

                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.serviceCentrePremium, locale)} passHref>
                                <Nav.Link active={isRoute(router.asPath, routes.serviceCentrePremium, locale)}>
                                    <FormattedMessage defaultMessage="Premium profil" />
                                </Nav.Link>
                            </Link>
                        </Nav.Item>

                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.serviceCentreSale, locale)} passHref>
                                <Nav.Link active={isRoute(router.asPath, routes.serviceCentreSale, locale)}>
                                    <FormattedMessage defaultMessage="Služby" />
                                </Nav.Link>
                            </Link>
                        </Nav.Item>

                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.priceList, locale)} passHref>
                                <Nav.Link active={isRoute(router.asPath, routes.priceList, locale)}>
                                    <FormattedMessage defaultMessage="Ceník inzerce" />
                                </Nav.Link>
                            </Link>
                        </Nav.Item>
                    </Nav>
                </Header.Nav>

                <Header.Toolbar>
                    <ButtonGroup>
                        <LoginRequiredAction initialPage={Generic}>
                            <Link href={getLocalizedRoute(routes.watchdogList, locale)} passHref shallow>
                                <Header.Button>
                                    <VisuallyHidden>
                                        <FormattedMessage defaultMessage="Hlídací pes" />
                                    </VisuallyHidden>
                                    {data?.user?.id && !!countData?.data?.user?.menuCounts?.watchdogs && (
                                        <ButtonBadge>
                                            <Badge>
                                                {countData.data.user.menuCounts?.watchdogs}
                                                <VisuallyHidden>
                                                    <FormattedMessage defaultMessage="Upozornění" />
                                                </VisuallyHidden>
                                            </Badge>
                                        </ButtonBadge>
                                    )}
                                    <Icon.Watchdog position="middle" />
                                </Header.Button>
                            </Link>
                        </LoginRequiredAction>
                    </ButtonGroup>

                    <ButtonGroup>
                        <LoginRequiredAction initialPage={Generic}>
                            <Link href={getLocalizedRoute(routes.favourites, locale)} passHref>
                                <Header.Button>
                                    <VisuallyHidden>
                                        <FormattedMessage defaultMessage={'Oblíbené'} />
                                    </VisuallyHidden>

                                    {data?.user?.id && !!countData?.data?.user?.menuCounts?.favourites && (
                                        <ButtonBadge>
                                            <Badge>
                                                {countData.data.user.menuCounts?.favourites}

                                                <VisuallyHidden>
                                                    <FormattedMessage defaultMessage={'označených'} />
                                                </VisuallyHidden>
                                            </Badge>
                                        </ButtonBadge>
                                    )}

                                    <Icon.Heart position="middle" />
                                </Header.Button>
                            </Link>
                        </LoginRequiredAction>
                    </ButtonGroup>

                    <Header.Cta>
                        <Link href={getLocalizedRoute(routes.serviceCentreIntroOwner, locale)} passHref>
                            <Button size="sm" variant="secondary">
                                <Typography as="span" variant="cta">
                                    <FormattedMessage defaultMessage={'Nabídnout nemovitost'} />
                                </Typography>

                                <Icon.Key position="end" />
                            </Button>
                        </Link>
                    </Header.Cta>

                    <ButtonGroup>
                        <Header.Button aria-label="Profile" onClick={() => setMenuSidebar((old) => !old)}>
                            {data?.user.image && (
                                <>
                                    <VisuallyHidden>
                                        <FormattedMessage defaultMessage={'Uživatelský profil'} />
                                    </VisuallyHidden>

                                    <Avatar size="xs">
                                        <NextImage src={data.user.image} width={28} height={28} alt={data.user.name} />
                                    </Avatar>
                                </>
                            )}

                            {data?.user && !data?.user.image && <Avatar iconName="Profile" size={'xs'} />}
                            {!data?.user && !data?.user.image && <Icon.Profile position="middle" />}
                        </Header.Button>

                        <Header.Button
                            aria-label="Menu"
                            onClick={() => (isTabletMobile ? setUserSidebar((old) => !old) : setMenuSidebar((old) => !old))}
                        >
                            <Icon.Menu position="middle" />
                        </Header.Button>
                    </ButtonGroup>
                </Header.Toolbar>
            </Header>

            <InteractionLoadable show={!data?.user && showMenuSidebar} keepRendered>
                <SidebarLogin show={!data?.user && showMenuSidebar} onHide={closeSidebar} />
            </InteractionLoadable>

            <InteractionLoadable show={!!data?.user && (showMenuSidebar || showUserSidebar)} keepRendered>
                <SidebarMenu
                    show={!!data?.user && (showMenuSidebar || showUserSidebar)}
                    onHide={closeSidebar}
                    unreadMessageCount={countData.data?.user?.menuCounts?.conversations}
                />
            </InteractionLoadable>

            <InteractionLoadable show={!data?.user && showUserSidebar} keepRendered>
                <SidebarMenuLogout show={!data?.user && showUserSidebar} onHide={closeSidebar} />
            </InteractionLoadable>
        </>
    );
};
